import { inject, Injectable, signal } from '@angular/core';
import { Canvas, Draw, DrawMap, Layer, Polyline } from 'leaflet';
import { L } from '../leaflet-setup/leaflet-setup';
import { DrawingManagerService } from '../virtualFence/drawing-manager.service';

@Injectable({ providedIn: 'root' })
export class MapDistanceMeasurementService {
  private drawingManagerService = inject(DrawingManagerService);

  private map: L.Map;
  readonly measurementCompleted = signal(false);

  private polylineDrawer: Draw.Polyline;
  private drawnLayer: Polyline | null = null;
  private distanceCanvasLayer: Canvas;

  initialize(map: L.Map) {
    this.map = map;
  }

  distanceMeasurement(status: boolean) {
    if (!status) return this.cleanupDistanceMeasurement();
    const activeMode = this.drawingManagerService.activeDrawingMode();
    if (activeMode) return;
    this.drawingManagerService.setActiveDrawingMode('distance-measurement');
    this.measurementCompleted.set(false);
    this.distanceCanvasLayer = L.canvas();
    this.map.addLayer(this.distanceCanvasLayer);

    this.polylineDrawer = new L.Draw.Polyline(this.map as DrawMap, {
      shapeOptions: {
        pane: 'drawPane',
        color: '#FFFF00',
        weight: 3,
        renderer: this.distanceCanvasLayer,
      },
    });
    this.polylineDrawer.enable();

    this.map.once('draw:created', this.handleDrawCreated);
    this.map.once('draw:drawstop', this.cleanupDistanceMeasurement);
  }

  private handleDrawCreated = (e: { layer: Layer }) => {
    this.map.off('draw:drawstop', this.cleanupDistanceMeasurement);

    if (e.layer instanceof L.Polyline) {
      e.layer.setStyle({ pane: 'drawPane' });
      this.drawnLayer = e.layer;
      this.map.addLayer(this.drawnLayer);

      const latlngs = this.drawnLayer.getLatLngs() as L.LatLng[];
      const totalDistance = this.calculateDistance(latlngs);
      const popupContent = this.formatDistance(totalDistance);

      this.drawnLayer.bindPopup(popupContent, { closeOnClick: false }).openPopup();
      this.drawnLayer.once('popupclose', this.cleanupDistanceMeasurement);
    }

    this.polylineDrawer?.disable();
  };

  private formatDistance(distance: number): string {
    if (distance < 1000) return `Distance: ${distance.toFixed(2)} m`;
    else {
      const distanceInKm = (distance / 1000).toFixed(2);
      return `Distance: ${distanceInKm} km`;
    }
  }

  private calculateDistance(latlngs: L.LatLng[]): number {
    let totalDistance = 0;
    if (latlngs.length > 1) {
      for (let i = 0; i < latlngs.length - 1; i++) {
        totalDistance += latlngs[i].distanceTo(latlngs[i + 1]);
      }
    }
    return totalDistance;
  }

  private cleanupDistanceMeasurement = () => {
    this.map.off('draw:created', this.handleDrawCreated);
    this.map.off('draw:drawstop', this.cleanupDistanceMeasurement);

    if (this.drawnLayer) {
      this.map.off('popupclose');
    }

    this.polylineDrawer?.disable();
    this.polylineDrawer = null;

    if (this.drawnLayer) {
      this.map.removeLayer(this.drawnLayer);
      this.drawnLayer = null;
    }

    if (this.distanceCanvasLayer) {
      this.map.removeLayer(this.distanceCanvasLayer);
      this.distanceCanvasLayer = null;
    }
    this.measurementCompleted.set(true);
    this.drawingManagerService.setActiveDrawingMode(null);
  };
}
